/**
 * PUBLIC pay-by-invoice client (2026-06-27) — used by the anonymous, web-hosted pay page
 * (app/(pay)/pay/<slug>). These endpoints take NO auth (the visitor is a customer, not a BEAM
 * user), so this uses a plain fetch against the API base rather than the authed client (which
 * attaches a Bearer token / CSRF). Shapes come from @beam/api-types. NEVER imports @smartbox/*.
 */
import { PublicOrgBrand, PublicInvoiceSummary, PublicCheckoutResponse } from '@beam/api-types';
import type {
  PublicOrgBrand as PublicOrgBrandType,
  PublicInvoiceSummary as PublicInvoiceSummaryType,
} from '@beam/api-types';
import { API_BASE_URL } from '../config';

const base = `${API_BASE_URL.replace(/\/+$/, '')}/api/pay`;

async function readJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

function errorMessage(body: unknown, fallback: string): string {
  const e = (body as { error?: { message?: string } })?.error;
  return e?.message ?? fallback;
}

export async function getPublicBrand(slug: string): Promise<PublicOrgBrandType> {
  const res = await fetch(`${base}/${encodeURIComponent(slug)}`);
  const body = await readJson(res);
  if (!res.ok) throw new Error(errorMessage(body, 'This pay page was not found.'));
  return PublicOrgBrand.parse(body);
}

/** Look up an invoice by number. Throws (404 → generic) when not payable. */
export async function getPublicInvoice(slug: string, number: string): Promise<PublicInvoiceSummaryType> {
  const res = await fetch(`${base}/${encodeURIComponent(slug)}/invoices/${encodeURIComponent(number)}`);
  const body = await readJson(res);
  if (!res.ok) throw new Error(errorMessage(body, 'Invoice not found. Check the number and try again.'));
  return PublicInvoiceSummary.parse(body);
}

/** Start a Stripe Checkout for an invoice; returns the hosted checkout URL to redirect to. */
export async function startPublicCheckout(slug: string, number: string): Promise<string> {
  const res = await fetch(`${base}/${encodeURIComponent(slug)}/invoices/${encodeURIComponent(number)}/checkout`, {
    method: 'POST',
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(errorMessage(body, 'Could not start payment. Please try again.'));
  return PublicCheckoutResponse.parse(body).checkoutUrl;
}

/** Resolve a custom host (pay.example.com) to its slug. Returns null when none. */
export async function resolveHostSlug(host: string): Promise<string | null> {
  const res = await fetch(`${base}/resolve?host=${encodeURIComponent(host)}`);
  if (!res.ok) return null;
  const body = (await readJson(res)) as { slug?: string };
  return typeof body.slug === 'string' ? body.slug : null;
}
